import React, { useState, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const HeroSection = () => {
  const [heroImage, setHeroImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchActiveHeroImage();
  }, []);

  const fetchActiveHeroImage = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/hero-images/active`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Hero image API response:', data);
        setHeroImage(data);
      } else if (response.status === 404) {
        // No active hero image found - use default
        setHeroImage(null);
      } else {
        throw new Error('Failed to fetch hero image');
      }
    } catch (error) {
      console.error('Error fetching hero image:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (heroImage) => {
    if (!heroImage) return null;
    
    // Use signedUrl if available (for S3 images)
    if (heroImage.signedUrl) {
      console.log('Using signed URL:', heroImage.signedUrl);
      return heroImage.signedUrl;
    }
    
    // Fallback to imageUrl for backward compatibility
    const imageUrl = heroImage.imageUrl;
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http') || imageUrl.startsWith('data:')) return imageUrl;
    const baseUrl = API_BASE_URL.replace('/api', '');
    const fullUrl = `${baseUrl}${imageUrl}`;
    console.log('Constructing image URL:', { imageUrl, baseUrl, fullUrl });
    return fullUrl;
  };

  if (loading) {
    return (
      <div className="relative h-96 bg-gradient-to-r from-primary-900 to-primary-700 animate-pulse">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-white text-lg">Loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative h-96 bg-gradient-to-r from-primary-900 to-primary-700">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-white text-lg">Unable to load hero image</div>
        </div>
      </div>
    );
  }

  // Default hero section when no hero image is set
  if (!heroImage) {
    return (
      <div className="relative h-96 bg-gradient-to-r from-primary-900 to-primary-700 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-repeat" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>
        
        {/* Content */}
        <div className="relative h-full flex items-center justify-center">
          <div className="text-center text-white px-4 max-w-4xl mx-auto">
            <p className="text-xl md:text-2xl mb-2 text-secondary-300">
              Eagles Believe, Eagles Achieve
            </p>
            <p className="text-lg md:text-xl opacity-90 mb-8">
              Excellence in Athletics, Character, and Competition
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/sports"
                className="bg-secondary-500 hover:bg-secondary-600 text-primary-900 font-bold py-3 px-8 rounded-lg transition-colors duration-200"
              >
                Explore Sports
              </a>
              <a
                href="/news"
                className="border-2 border-white text-white hover:bg-white hover:text-primary-900 font-bold py-3 px-8 rounded-lg transition-colors duration-200"
              >
                Latest News
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Custom hero section with uploaded image
  return (
    <div className="relative h-96 md:h-[500px] lg:h-[600px] overflow-hidden">
      {/* Hero Image */}
      <div className="absolute inset-0">
        <img
          src={getImageUrl(heroImage)}
          alt={heroImage.title}
          className="w-full h-full object-cover"
          onError={(e) => {
            console.error('Hero image failed to load:', getImageUrl(heroImage));
            setError('Unable to load hero image');
          }}
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-40" />
      </div>
      
      {/* Content */}
      <div className="relative h-full flex items-center justify-center">
        <div className="text-center text-white px-4 max-w-4xl mx-auto">
          {heroImage.subtitle && (
            <p className="text-lg md:text-xl opacity-90 mb-8 drop-shadow-md">
              {heroImage.subtitle}
            </p>
          )}
          {heroImage.buttonText && heroImage.buttonLink && (
            <div className="flex justify-center">
              <a
                href={heroImage.buttonLink}
                className="bg-secondary-500 hover:bg-secondary-600 text-primary-900 font-bold py-3 px-8 rounded-lg transition-colors duration-200 drop-shadow-lg"
              >
                {heroImage.buttonText}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeroSection;